import * as React from 'react';
import { cn } from '@lib/cn';

export interface ComboboxProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'placeholder' | 'size'
> {
  label?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

const Combobox = React.forwardRef<HTMLSelectElement, ComboboxProps>(
  (
    { className, label, options, id, placeholder, variant = 'default', size = 'md', ...props },
    ref,
  ) => {
    const selectId = id || props.name;

    const baseStyles =
      'appearance-none bg-transparent font-inter font-medium text-base leading-base text-[--text-dark-primary] outline-none focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 [color-scheme:dark] [&>option]:bg-[var(--base-surface-1,#141414)] [&>option]:text-[var(--text-dark-primary,#f5f5f5)]';

    const wrapperStyles =
      'flex items-center gap-[--spacing-5] rounded-[--radi-6] border transition-colors';

    const variants = {
      default:
        'bg-[--base-surface-1] border-[--base-border] focus-within:border-[var(--state-brand-active,#36d399)]',

      outline: 'bg-transparent border-2 border-[var(--state-brand-active,#36d399)]',

      ghost:
        'bg-transparent border-transparent hover:bg-[--base-fill-1] focus-within:border-[var(--state-brand-active,#36d399)]',
    };

    const sizes = {
      lg: 'h-[--spacing-17] pr-[--spacing-8] pl-[--spacing-9] gap-[--spacing-7] text-lg leading-lg',
      md: 'h-[--spacing-15] px-[--spacing-6]',
      sm: 'h-[--spacing-13] px-[--spacing-5] text-sm leading-sm',
    };

    return (
      <div>
        {label && (
          <label
            htmlFor={selectId}
            className="mb-2 block font-inter text-base font-medium leading-6 text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]"
          >
            {label}
          </label>
        )}

        <div className={cn(wrapperStyles, variants[variant], sizes[size])}>
          <select
            id={selectId}
            ref={ref}
            className={cn(baseStyles, 'min-w-0 flex-1', className)}
            {...props}
          >
            {placeholder && (
              <option value="" className="text-[var(--text-dark-tertiary,rgba(255,255,255,0.36))]">
                {placeholder}
              </option>
            )}

            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <i className="ri-arrow-down-s-line shrink-0 pointer-events-none text-[24px] text-[--base-fill-4]"></i>
        </div>
      </div>
    );
  },
);

Combobox.displayName = 'Combobox';

export { Combobox };
